import { useMemo, useState } from "react";
import { m, useIsPresent } from "motion/react";
import { NumeroAnimado, RotuloDeEstado, TrocaDeRotulo, useMovimento } from "@vertex/ui/movimento";
import {
  brl,
  parseMoney,
  roundMoney,
  sugestoesDeNotas,
  type PdvCheckoutMethod,
} from "@vertex/validators/pdv-pure";
import { buttonClass } from "@vertex/ui/primitives/button-style";
import { useKeyScope } from "../keys/registry";
import type { PdvContext } from "../lib/pdv";

const ROTULO: Record<PdvCheckoutMethod, string> = {
  cash: "Dinheiro",
  pix: "Pix",
  card: "Cartão",
};

/**
 * Pagamento. Camada própria em vez do `Dialog` de propósito: esta é a tela
 * mais quente do terminal (abre em toda venda) e o `Dialog` traz o módulo
 * dele junto. O movimento é o do sistema, com os `m.*` que o provider já
 * carrega (VOS Movimento, 26/09): o véu esmaece (`fundo`), o painel cresce de
 * .97 (`modal`) e sai encolhendo; o troco ROLA enquanto o operador digita; o
 * Confirmar diz "Registrando" com o girador no lugar. Nada disso espera: o
 * campo já tem o foco no quadro em que o painel abre.
 *
 * O foco vai pro campo de valor recebido assim que abre: o operador digita
 * "50" e lê o troco sem tocar no mouse.
 */
export function PaymentPanel({
  total,
  ctx,
  onCancel,
  onConfirm,
}: {
  total: number;
  ctx: PdvContext;
  onCancel: () => void;
  onConfirm: (method: PdvCheckoutMethod) => Promise<void>;
}) {
  const aceitos = useMemo(() => {
    const permitidos = ctx.paymentMethods.filter((m): m is PdvCheckoutMethod =>
      (["cash", "pix", "card"] as string[]).includes(m),
    );
    /* Org sem configuração nenhuma ainda vende: sem este fallback o balcão
       abriria o pagamento sem botão nenhum. */
    return permitidos.length > 0 ? permitidos : (["cash", "pix", "card"] as PdvCheckoutMethod[]);
  }, [ctx.paymentMethods]);

  const mov = useMovimento();
  const fundo = mov.r.fundo();
  /* Saindo (Esc, Cancelar), o painel já não é da pessoa: não pega clique nem
     tecla. Sem isto um F3 nos 140ms da saída fecharia a venda cancelada. */
  const presente = useIsPresent();
  const [method, setMethod] = useState<PdvCheckoutMethod>(aceitos[0] ?? "cash");
  const [recebidoRaw, setRecebido] = useState("");
  const [busy, setBusy] = useState(false);

  const recebido = parseMoney(recebidoRaw);
  const troco = roundMoney(Math.max(0, recebido - total));
  const falta = roundMoney(Math.max(0, total - recebido));
  /* Só dinheiro exige conferir o recebido; pix e cartão fecham pelo valor. */
  const podeFechar = method !== "cash" || recebido >= total;

  /**
   * Escopo próprio, empurrado por cima do da venda. Enquanto ele está no topo,
   * o F3 da venda não responde — é o que impede um segundo checkout sobre o
   * primeiro. O `back` continua atravessando a pilha por definição do registry.
   */
  useKeyScope(
    "payment",
    {
      back: () => {
        onCancel();
        return true;
      },
      checkout: () => {
        void confirmar();
        return true;
      },
    },
    presente,
  );

  async function confirmar() {
    if (busy || !podeFechar || !presente) return;
    setBusy(true);
    try {
      await onConfirm(method);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Pagamento"
      className="fixed inset-0 z-40 flex items-center justify-center p-6"
      style={presente ? undefined : { pointerEvents: "none" }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      {/* O véu é uma camada própria: é ele que esmaece, e o clique fora
          continua sendo no contêiner (o véu não pega ponteiro). */}
      <m.div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: "var(--overlay)" }}
        initial={fundo.initial}
        animate={fundo.animate}
        exit={fundo.exit}
      />
      <m.div
        className="relative w-full max-w-[520px] rounded-xl p-6"
        style={{ background: "var(--panel)", border: "1px solid var(--line)", boxShadow: "var(--shadow-lg)" }}
        initial={mov.r.modal.initial}
        animate={mov.r.modal.animate}
        exit={mov.r.modal.exit}
      >
        <div className="mb-5 flex items-baseline justify-between">
          <span style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)" }}>Total a pagar</span>
          <strong style={{ fontSize: "var(--fs-kpi)", letterSpacing: "-0.02em" }}>
            <NumeroAnimado valor={total} formatar={brl} />
          </strong>
        </div>

        <div className="mb-5 grid grid-cols-3 gap-2">
          {aceitos.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMethod(m)}
              className={buttonClass(m === method ? "primary" : "secondary", "lg")}
            >
              {ROTULO[m]}
            </button>
          ))}
        </div>

        {method === "cash" ? (
          <>
            <div className="pdv-field mb-3">
              <label htmlFor="pdv-recebido">Valor recebido</label>
              <input
                id="pdv-recebido"
                autoFocus
                inputMode="decimal"
                value={recebidoRaw}
                onChange={(e) => setRecebido(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void confirmar();
                }}
                placeholder="0,00"
              />
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
              {sugestoesDeNotas(total).map((v) => (
                <button
                  key={v}
                  type="button"
                  className={buttonClass("subtle", "sm")}
                  onClick={() => setRecebido(String(v).replace(".", ","))}
                >
                  {brl(v)}
                </button>
              ))}
            </div>

            {/* Falta → Troco troca NO LUGAR: a cor anda (CSS), o rótulo troca
                de face e o valor ROLA enquanto o operador digita. */}
            <div
              className="pdv-troco mb-5 flex items-baseline justify-between rounded-xl px-4 py-3"
              style={{
                background: falta > 0 ? "var(--warn-soft)" : "var(--ok-soft)",
                color: falta > 0 ? "var(--warn)" : "var(--ok)",
              }}
            >
              <span style={{ fontSize: "var(--fs-sm)" }}>
                <TrocaDeRotulo chave={falta > 0 ? "falta" : "troco"}>{falta > 0 ? "Falta" : "Troco"}</TrocaDeRotulo>
              </span>
              <strong style={{ fontSize: "var(--fs-h3)" }}>
                <NumeroAnimado valor={falta > 0 ? falta : troco} formatar={brl} />
              </strong>
            </div>
          </>
        ) : null}

        <div className="flex gap-2">
          <button type="button" className={`${buttonClass("secondary", "lg")} flex-1`} onClick={onCancel}>
            Cancelar
          </button>
          <button
            type="button"
            disabled={busy || !podeFechar}
            aria-busy={busy || undefined}
            aria-label={busy ? "Registrando" : "Confirmar"}
            className={`${buttonClass("primary", "lg")} flex-1`}
            onClick={() => void confirmar()}
          >
            <RotuloDeEstado
              estado={busy ? "fazendo" : "parado"}
              alinhar="centro"
              rotulos={{ parado: "Confirmar", fazendo: "Registrando", feito: "Registrado" }}
            />
          </button>
        </div>
      </m.div>
    </div>
  );
}
